// ============================================================================
// IMPORT/EXPORT MODAL - Modal reutilizable para importar/exportar Excel
// ============================================================================

import { useState, useCallback } from 'react';
import {
  Download,
  Upload,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  X,
} from 'lucide-react';
import { Modal, ModalFooter } from './Modal';
import { FileDropzone } from './FileDropzone';

// Tipos (definidos aquí para evitar dependencias circulares)
export type ImportMode = 'create_only' | 'update_only' | 'create_or_update';

export interface PreviewRow {
  rowNumber: number;
  data: Record<string, any>;
  status: 'new' | 'update' | 'error' | 'skip';
  errors: string[];
  existingId?: string;
}

export interface ImportPreview {
  valid: boolean;
  totalRows: number;
  newRows: number;
  updateRows: number;
  errorRows: number;
  rows: PreviewRow[];
}

export interface ImportResult {
  success: boolean;
  created: number;
  updated: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

interface ImportExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  // Funciones de API
  onExport: (includeInactive: boolean) => Promise<void>;
  onDownloadTemplate?: () => Promise<void>;
  onPreview?: (file: File) => Promise<ImportPreview>;
  onImport: (file: File, mode: ImportMode) => Promise<ImportResult>;
  // Callback después de importar
  onImportSuccess?: () => void;
  // Columnas para mostrar en preview (opcional)
  previewColumns?: { key: string; label: string }[];
}

type Step = 'options' | 'preview' | 'result';

export function ImportExportModal({
  isOpen,
  onClose,
  title,
  onExport,
  onDownloadTemplate,
  onPreview,
  onImport,
  onImportSuccess,
  previewColumns,
}: ImportExportModalProps) {
  const [step, setStep] = useState<Step>('options');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [importMode, setImportMode] = useState<ImportMode>('create_or_update');
  const [result, setResult] = useState<ImportResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportIncludeInactive, setExportIncludeInactive] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const resetState = useCallback(() => {
    setStep('options');
    setSelectedFile(null);
    setPreview(null);
    setResult(null);
    setIsLoading(false);
    setErrorMessage(null);
  }, []);

  const handleClose = useCallback(() => {
    if (!isLoading && !isExporting) {
      resetState();
      onClose();
    }
  }, [isLoading, isExporting, resetState, onClose]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    setErrorMessage(null);
    try {
      await onExport(exportIncludeInactive);
    } catch (error: any) {
      console.error('Error exporting:', error);
      setErrorMessage(error.message || 'Error al exportar');
    } finally {
      setIsExporting(false);
    }
  }, [onExport, exportIncludeInactive]);

  const handleDownloadTemplate = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      await onDownloadTemplate?.();
    } catch (error: any) {
      console.error('Error downloading template:', error);
      setErrorMessage(error.message || 'Error al descargar plantilla');
    } finally {
      setIsLoading(false);
    }
  }, [onDownloadTemplate]);

  const handleFileSelect = useCallback(async (file: File) => {
    setSelectedFile(file);
    setErrorMessage(null);
    // Modo simple (sin preview): solo se selecciona; se importa con el boton Importar del paso de opciones.
    if (!onPreview) return;
    setIsLoading(true);
    try {
      const previewData = await onPreview(file);
      setPreview(previewData);
      setStep('preview');
    } catch (error: any) {
      console.error('Error previewing:', error);
      setPreview({
        valid: false,
        totalRows: 0,
        newRows: 0,
        updateRows: 0,
        errorRows: 1,
        rows: [{
          rowNumber: 0,
          data: {},
          status: 'error',
          errors: [error.message || 'Error al procesar el archivo'],
        }],
      });
      setStep('preview');
    } finally {
      setIsLoading(false);
    }
  }, [onPreview]);

  const handleImport = useCallback(async () => {
    if (!selectedFile) return;
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const importResult = await onImport(selectedFile, importMode);
      setResult(importResult);
      setStep('result');
      if (importResult.success) {
        onImportSuccess?.();
      }
    } catch (error: any) {
      console.error('Error importing:', error);
      setResult({
        success: false,
        created: 0,
        updated: 0,
        skipped: 0,
        errors: [{ row: 0, message: error.message || 'Error al importar' }],
      });
      setStep('result');
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, importMode, onImport, onImportSuccess]);

  const getStatusIcon = (status: PreviewRow['status']) => {
    switch (status) {
      case 'new':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Nuevo</span>;
      case 'update':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Actualizar</span>;
      case 'error':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">Error</span>;
      case 'skip':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">Omitir</span>;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Importar / Exportar - ${title}`}
      size="lg"
    >
      {/* Mensaje de error global */}
      {errorMessage && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertTriangle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{errorMessage}</span>
          <button onClick={() => setErrorMessage(null)} className="ml-auto p-1 hover:bg-red-100 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* PASO 1: Opciones */}
      {step === 'options' && (
        <div className="space-y-6">
          {/* Exportar */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Download className="h-5 w-5 text-blue-600" />
              Exportar datos
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Descarga un archivo Excel con todos los datos actuales.
            </p>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={exportIncludeInactive}
                  onChange={(e) => setExportIncludeInactive(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600"
                />
                <span className="text-gray-700">Incluir inactivos</span>
              </label>
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
              >
                {isExporting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Exportando...</>
                ) : (
                  <><Download className="h-4 w-4" /> Descargar Excel</>
                )}
              </button>
            </div>
          </div>

          {/* Separador */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-gray-500">o</span>
            </div>
          </div>

          {/* Importar */}
          <div className="p-4 bg-gray-50 rounded-xl">
            <h3 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
              <Upload className="h-5 w-5 text-green-600" />
              Importar datos
            </h3>
            <p className="text-sm text-gray-600 mb-3">
              Sube un archivo Excel para crear o actualizar registros.
            </p>

            {onDownloadTemplate && (
              <button
                onClick={handleDownloadTemplate}
                disabled={isLoading}
                className="mb-4 text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 disabled:opacity-50"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Descargar plantilla vacía
              </button>
            )}

            <FileDropzone
              onFileSelect={handleFileSelect}
              selectedFile={selectedFile}
              onClear={() => setSelectedFile(null)}
              disabled={isLoading}
            />

            {isLoading && onPreview && (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-600">
                <Loader2 className="h-4 w-4 animate-spin" />
                Analizando archivo...
              </div>
            )}

            {!onPreview && selectedFile && (
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleImport}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isLoading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Importando...</>
                  ) : (
                    <><Upload className="h-4 w-4" /> Importar</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* PASO 2: Preview */}
      {step === 'preview' && preview && (
        <div className="space-y-4">
          {/* Resumen */}
          <div className="grid grid-cols-4 gap-3">
            <div className="p-3 bg-gray-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-gray-900">{preview.totalRows}</p>
              <p className="text-xs text-gray-500">Total filas</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-green-600">{preview.newRows}</p>
              <p className="text-xs text-green-600">Nuevos</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-blue-600">{preview.updateRows}</p>
              <p className="text-xs text-blue-600">Actualizar</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg text-center">
              <p className="text-2xl font-bold text-red-600">{preview.errorRows}</p>
              <p className="text-xs text-red-600">Errores</p>
            </div>
          </div>

          {/* Modo de importación */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <label className="block text-sm font-medium text-amber-800 mb-2">
              ¿Qué hacer con los registros?
            </label>
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="importMode"
                  value="create_or_update"
                  checked={importMode === 'create_or_update'}
                  onChange={() => setImportMode('create_or_update')}
                  className="text-amber-600"
                />
                <span className="text-sm text-gray-700">Crear nuevos y actualizar existentes</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="importMode"
                  value="create_only"
                  checked={importMode === 'create_only'}
                  onChange={() => setImportMode('create_only')}
                  className="text-amber-600"
                />
                <span className="text-sm text-gray-700">Solo crear nuevos</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="importMode"
                  value="update_only"
                  checked={importMode === 'update_only'}
                  onChange={() => setImportMode('update_only')}
                  className="text-amber-600"
                />
                <span className="text-sm text-gray-700">Solo actualizar existentes</span>
              </label>
            </div>
          </div>

          {/* Tabla de preview */}
          <div className="max-h-64 overflow-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Fila</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Estado</th>
                  {previewColumns ? (
                    previewColumns.map((col) => (
                      <th key={col.key} className="px-3 py-2 text-left text-xs font-semibold text-gray-600">
                        {col.label}
                      </th>
                    ))
                  ) : (
                    <>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Código</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Nombre</th>
                    </>
                  )}
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Errores</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {preview.rows.slice(0, 50).map((row) => (
                  <tr key={row.rowNumber} className={row.status === 'error' ? 'bg-red-50' : ''}>
                    <td className="px-3 py-2 text-gray-500">{row.rowNumber}</td>
                    <td className="px-3 py-2">{getStatusIcon(row.status)}</td>
                    {previewColumns ? (
                      previewColumns.map((col) => (
                        <td key={col.key} className="px-3 py-2 text-gray-900">
                          {String(row.data[col.key] ?? '-')}
                        </td>
                      ))
                    ) : (
                      <>
                        <td className="px-3 py-2 text-gray-900 font-mono">{row.data.code || '-'}</td>
                        <td className="px-3 py-2 text-gray-900">{row.data.name || '-'}</td>
                      </>
                    )}
                    <td className="px-3 py-2 text-red-600 text-xs">
                      {row.errors.length > 0 ? row.errors.join(', ') : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.rows.length > 50 && (
              <p className="p-2 text-center text-xs text-gray-500 bg-gray-50">
                Mostrando 50 de {preview.rows.length} filas
              </p>
            )}
          </div>

          <ModalFooter>
            <button
              onClick={() => setStep('options')}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Volver
            </button>
            <button
              onClick={handleImport}
              disabled={isLoading || preview.errorRows > 0}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isLoading ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Importando...</>
              ) : (
                <><Upload className="h-4 w-4" /> Importar {preview.totalRows - preview.errorRows} registros</>
              )}
            </button>
          </ModalFooter>
        </div>
      )}

      {/* PASO 3: Resultado */}
      {step === 'result' && result && (
        <div className="space-y-4">
          <div className={`p-6 rounded-xl text-center ${result.success ? 'bg-green-50' : 'bg-red-50'}`}>
            {result.success ? (
              <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
            ) : (
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            )}
            <h3 className={`text-xl font-bold mb-2 ${result.success ? 'text-green-800' : 'text-red-800'}`}>
              {result.success ? '¡Importación completada!' : 'Importación con errores'}
            </h3>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{result.created}</p>
              <p className="text-xs text-green-600">Creados</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{result.updated}</p>
              <p className="text-xs text-blue-600">Actualizados</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-600">{result.skipped}</p>
              <p className="text-xs text-gray-600">Omitidos</p>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <h4 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Errores ({result.errors.length})
              </h4>
              <ul className="text-sm text-red-700 space-y-1 max-h-32 overflow-auto">
                {result.errors.map((err, i) => (
                  <li key={i}>Fila {err.row}: {err.message}</li>
                ))}
              </ul>
            </div>
          )}

          <ModalFooter>
            <button
              onClick={resetState}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Nueva importación
            </button>
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Cerrar
            </button>
          </ModalFooter>
        </div>
      )}
    </Modal>
  );
}

export default ImportExportModal;
